import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Truck, MapPin, CreditCard, ExternalLink } from 'lucide-react';
import { orderService } from '../../services/orderService';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Skeleton from '../../components/common/Skeleton';
import StatusBadge from '../../components/common/StatusBadge';
import EmptyState from '../../components/common/EmptyState';
import ImageWithFallback from '../../components/common/ImageWithFallback';
import OrderTimeline from '../../components/order/OrderTimeline';
import { formatPrice } from '../../utils/priceCalculator';
import { formatDateTime } from '../../utils/formatRelativeTime';
import { formatOrderNumber } from '../../utils/statusBucket';

// Usado em dois lugares:
//   - /revendedor/painel/pedidos/:id  (reseller vendo pedido do cliente dele)
//   - /minha-conta/pedidos/:id        (cliente vendo o próprio pedido)
// O backend aplica RBAC; o front só apresenta. Detecta a origem pelo
// pathname pra escolher o link "voltar" correto.
const ResellerOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const isClientView = window.location.pathname.startsWith('/minha-conta');
  const backPath = isClientView ? '/minha-conta/pedidos' : '/revendedor/painel/pedidos';

  const loadOrder = useCallback(async () => {
    try {
      setLoading(true);
      const data = await orderService.getById(id);
      setOrder(data);
    } catch {
      navigate(backPath);
    } finally {
      setLoading(false);
    }
  }, [id, navigate, backPath]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto space-y-4">
        <Skeleton variant="text" width="33%" />
        <Skeleton variant="block" height={240} />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="max-w-3xl mx-auto">
        <EmptyState title="Pedido não encontrado" description="O pedido pode ter sido removido." />
      </div>
    );
  }

  const shipping = order.shippingAddress;
  const payment = Array.isArray(order.payments) ? order.payments[0] : order.Payment;

  return (
    <div className="max-w-5xl mx-auto">
      <button
        type="button"
        onClick={() => navigate(backPath)}
        className="inline-flex items-center gap-2 text-sm text-app-text-secondary hover:text-app-text transition-colors mb-4"
      >
        <ArrowLeft size={16} /> Voltar
      </button>

      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-3xl font-extrabold text-app-text mb-1">
            Pedido {formatOrderNumber(order)}
          </h1>
          <p className="text-sm text-app-text-secondary">
            Criado em {formatDateTime(order.createdAt)}
          </p>
        </div>
        <StatusBadge domain="order" status={order.statusBucket || order.status} size="md" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Timeline */}
          <Card className="p-6">
            <h2 className="text-lg font-bold text-app-text mb-4">Linha do tempo</h2>
            <OrderTimeline order={order} />
          </Card>

          {/* Itens */}
          <Card className="p-6">
            <h2 className="text-lg font-bold text-app-text mb-4">Itens</h2>
            <ul className="divide-y divide-app-border">
              {(order.items || []).map((item) => (
                <li key={item.id} className="flex gap-4 py-4 first:pt-0 last:pb-0">
                  <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-app-surface-hover">
                    <ImageWithFallback
                      src={item.imageUrl}
                      alt={item.productName}
                      fallbackText={item.productName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-app-text truncate">{item.productName}</h3>
                    {item.variationLabel && (
                      <p className="text-sm text-app-text-secondary">{item.variationLabel}</p>
                    )}
                    <p className="text-xs text-app-text-secondary mt-1">
                      {item.quantity} × {formatPrice(item.unitPrice)}
                    </p>
                    {!isClientView && parseFloat(item.unitDiscount ?? item.unitCommission ?? 0) > 0 && (
                      <p className="text-xs text-success mt-0.5">
                        Comissão unitária: +{formatPrice(item.unitDiscount ?? item.unitCommission)}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-app-text">
                      {formatPrice(item.unitPrice * item.quantity)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </Card>

          {/* Endereço */}
          {shipping && (
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <MapPin size={18} className="text-primary" />
                <h2 className="text-lg font-bold text-app-text">Endereço de entrega</h2>
              </div>
              <p className="text-sm text-app-text">
                {shipping.street}, {shipping.number || 's/n'}
                {shipping.complement && ` — ${shipping.complement}`}
              </p>
              <p className="text-sm text-app-text-secondary">
                {shipping.district ? `${shipping.district}, ` : ''}
                {shipping.city} - {shipping.state} · CEP {shipping.zip}
              </p>
            </Card>
          )}

          {/* Pagamento */}
          {payment && (
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <CreditCard size={18} className="text-primary" />
                <h2 className="text-lg font-bold text-app-text">Pagamento</h2>
              </div>
              <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                <span className="text-sm text-app-text">
                  {payment.methodLabel || 'Cobrança'} · {formatPrice(parseFloat(payment.amount))}
                </span>
                <StatusBadge domain="payment" status={payment.status} size="sm" />
              </div>
              <div className="flex flex-wrap gap-2">
                {/* "Pagar agora" abre o checkout do gateway. Só aparece se o
                    pagamento ainda está pendente e o adapter persistiu URL. */}
                {payment.invoiceUrl && payment.status === 'pending' && (
                  <a
                    href={payment.invoiceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button size="sm" rightIcon={<ExternalLink size={14} />}>
                      Pagar agora
                    </Button>
                  </a>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    navigate(
                      isClientView
                        ? `/minha-conta/pedidos/${order.id}`
                        : `/revendedor/painel/pagamentos/${payment.id}`
                    )
                  }
                >
                  Ver detalhes do pagamento
                </Button>
              </div>
            </Card>
          )}

          {/* Tracking */}
          {order.trackingCode && (
            <Card className="p-6 bg-info/5 border-info/30">
              <div className="flex items-center gap-2 mb-1">
                <Truck size={18} className="text-info" />
                <h2 className="text-lg font-bold text-app-text">Rastreamento</h2>
              </div>
              <p className="text-sm text-app-text">
                Código:{' '}
                <span className="font-mono font-semibold">{order.trackingCode}</span>
              </p>
            </Card>
          )}

          {order.status === 'rejected' && order.rejectionReason && (
            <Card className="p-6 bg-error/5 border-error/30">
              <h2 className="text-lg font-bold text-error mb-1">Motivo da recusa</h2>
              <p className="text-sm text-error/80">{order.rejectionReason}</p>
            </Card>
          )}
        </div>

        {/* Sidebar resumo */}
        <div>
          <Card className="p-6 lg:sticky lg:top-20">
            <h2 className="text-lg font-bold text-app-text mb-4">Resumo</h2>
            <dl className="space-y-2 text-sm mb-4">
              <div className="flex justify-between">
                <dt className="text-app-text-secondary">Subtotal</dt>
                <dd>{formatPrice(order.subtotal)}</dd>
              </div>
              {!isClientView && parseFloat(order.totalDiscount ?? order.totalCommission ?? 0) > 0 && (
                <div className="flex justify-between text-success">
                  <dt>Comissão</dt>
                  <dd className="font-semibold">+{formatPrice(order.totalDiscount ?? order.totalCommission)}</dd>
                </div>
              )}
              {parseFloat(order.shippingCost || 0) > 0 && (
                <div className="flex justify-between">
                  <dt className="text-app-text-secondary">Frete</dt>
                  <dd>{formatPrice(order.shippingCost)}</dd>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-app-border font-bold text-lg">
                <dt>Total</dt>
                <dd className="text-primary">{formatPrice(order.totalToPay)}</dd>
              </div>
            </dl>
            {order.notes && (
              <div className="pt-4 border-t border-app-border">
                <p className="text-xs font-semibold text-app-text-secondary uppercase tracking-wide mb-1">
                  Observações
                </p>
                <p className="text-sm text-app-text whitespace-pre-line">{order.notes}</p>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ResellerOrderDetail;
